import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Image from '~/components/Common/Image';
import MarkdownEditor from '~/components/Common/MarkdownEditor';

import { FlatImageExtraField, FlatImageExtraFieldValue, FlatImageItem } from './types';

function getMinimumTileHeight(showDescriptionField: boolean, extraFields: Array<FlatImageExtraField>) {
  return extraFields.reduce(
    (height, field) => height + (field.type === 'markdown' ? 260 : field.type === 'checkbox' ? 40 : 72),
    320 + (showDescriptionField ? 72 : 0),
  );
}

type SortableResult = ReturnType<typeof useSortable>;

interface Props {
  item: FlatImageItem;
  customActions?: React.ReactNode;
  descriptionLabel: string;
  disabled: boolean;
  extraFields: Array<FlatImageExtraField>;
  onDelete: (itemId: string | number) => void;
  onDescriptionChange: (itemId: string | number, value: string) => void;
  onExtraFieldChange: (itemId: string | number, fieldKey: string, value: FlatImageExtraFieldValue) => void;
  onSetHero?: (itemId: string | number) => void;
  onToggleHidden?: (itemId: string | number) => void;
  showDeleteAction: boolean;
  showDescriptionField: boolean;
  showHeroAction: boolean;
  showHiddenToggle: boolean;
  showReorderAction: boolean;
  deleteActionLabel: string;
  restoreActionLabel: string;
}

interface TileHeaderProps {
  attributes: SortableResult['attributes'];
  customActions?: React.ReactNode;
  deleteActionLabel: string;
  disabled: boolean;
  isDragging: boolean;
  item: FlatImageItem;
  listeners: SortableResult['listeners'];
  onDelete: (itemId: string | number) => void;
  onSetHero?: (itemId: string | number) => void;
  onToggleHidden?: (itemId: string | number) => void;
  restoreActionLabel: string;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  showDeleteAction: boolean;
  showHeroAction: boolean;
  showHiddenToggle: boolean;
  showReorderAction: boolean;
}

function renderTileHeader({
  attributes,
  customActions,
  deleteActionLabel,
  disabled,
  isDragging,
  item,
  listeners,
  onDelete,
  onSetHero,
  onToggleHidden,
  restoreActionLabel,
  setActivatorNodeRef,
  showDeleteAction,
  showHeroAction,
  showHiddenToggle,
  showReorderAction,
}: TileHeaderProps) {
  const canDeleteItem = showDeleteAction && item.canDelete !== false;
  const canReorderItem = showReorderAction && item.canReorder !== false;
  const showSetHeroAction = showHeroAction && onSetHero && !item.isHero;
  const showHiddenAction = showHiddenToggle && !!onToggleHidden;

  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
      <Stack alignItems="center" direction="row" spacing={1} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
        <Chip label={`#${item.order}`} size="small" />
        {item.isHero ? <Chip color="warning" icon={<StarOutlineIcon />} label="Hero" size="small" /> : null}
        {canDeleteItem ? (
          item.isPendingDelete ? (
            <Button
              aria-label={restoreActionLabel}
              color="primary"
              disabled={disabled}
              size="small"
              startIcon={<UndoOutlinedIcon fontSize="small" />}
              variant="text"
              onClick={() => onDelete(item.id)}
            >
              Undo
            </Button>
          ) : (
            <IconButton
              aria-label={deleteActionLabel}
              color="error"
              disabled={disabled}
              size="small"
              onClick={() => onDelete(item.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )
        ) : null}
        {showSetHeroAction ? (
          <Button
            disabled={disabled || item.isPendingDelete}
            size="small"
            variant="text"
            onClick={() => onSetHero(item.id)}
          >
            Make hero
          </Button>
        ) : null}
        {showHiddenAction ? (
          <Button
            disabled={disabled || item.isPendingDelete}
            size="small"
            startIcon={item.isHidden ? <VisibilityRoundedIcon /> : <VisibilityOffRoundedIcon />}
            variant="text"
            onClick={() => onToggleHidden(item.id)}
          >
            {item.isHidden ? 'Show image' : 'Hide image'}
          </Button>
        ) : null}
      </Stack>
      <Stack alignItems="center" direction="row" spacing={0.5}>
        {customActions}
        {canReorderItem ? (
          <IconButton
            {...attributes}
            {...listeners}
            ref={setActivatorNodeRef}
            aria-label="Reorder image"
            disabled={disabled}
            size="small"
            sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <DragIndicatorIcon />
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  );
}

export default function FlatImageTile({
  item,
  customActions,
  descriptionLabel,
  disabled,
  extraFields,
  onDelete,
  onDescriptionChange,
  onExtraFieldChange,
  onSetHero,
  onToggleHidden,
  showDeleteAction,
  showDescriptionField,
  showHeroAction,
  showHiddenToggle,
  showReorderAction,
  deleteActionLabel,
  restoreActionLabel,
}: Props) {
  const { attributes, isDragging, listeners, setActivatorNodeRef, setNodeRef, transform, transition } = useSortable({
    id: item.id,
    disabled: disabled || !showReorderAction || item.canReorder === false,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const minTileHeight = getMinimumTileHeight(showDescriptionField, extraFields);

  const canPreviewWithPublicId = !!item.publicImageId;
  const preview = canPreviewWithPublicId ? (
    <Image
      alt={item.description || item.filename || item.displayId || 'image preview'}
      publicId={item.publicImageId}
      options={{ width: 640 }}
      style={{ display: 'block', height: '100%', objectFit: 'contain', width: '100%' }}
    />
  ) : (
    <Box
      component="img"
      alt={item.description || item.filename || item.displayId || 'image preview'}
      src={item.previewUrl}
      sx={{ display: 'block', height: '100%', objectFit: 'contain', width: '100%' }}
    />
  );

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        alignSelf: 'start',
        minHeight: minTileHeight,
        opacity: item.isPendingDelete ? 0.55 : 1,
        borderColor: item.isHero ? 'warning.main' : 'divider',
        boxShadow: item.isHero ? 2 : 0,
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        {renderTileHeader({
          attributes,
          customActions,
          deleteActionLabel,
          disabled,
          isDragging,
          item,
          listeners,
          restoreActionLabel,
          setActivatorNodeRef,
          showDeleteAction,
          showHeroAction,
          showHiddenToggle,
          showReorderAction,
          onDelete,
          onSetHero,
          onToggleHidden,
        })}

        <Box
          sx={{
            alignItems: 'center',
            aspectRatio: '16 / 9',
            display: 'flex',
            borderRadius: 1,
            justifyContent: 'center',
            overflow: 'hidden',
            bgcolor: 'grey.100',
            position: 'relative',
            '& img': item.isHidden
              ? {
                  filter: 'grayscale(1)',
                  opacity: 0.45,
                }
              : undefined,
          }}
        >
          {preview}
          {item.isHidden ? (
            <Chip
              label="Hidden image"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                bgcolor: 'rgba(33, 33, 33, 0.78)',
                color: 'common.white',
                fontWeight: 600,
              }}
            />
          ) : null}
        </Box>

        <Stack spacing={0.5}>
          <Typography noWrap variant="subtitle2">
            {item.displayId || item.filename || item.id}
          </Typography>
          {item.filename && item.filename !== item.displayId ? (
            <Typography color="text.secondary" noWrap variant="body2">
              {item.filename}
            </Typography>
          ) : null}
        </Stack>

        {showDescriptionField ? (
          <TextField
            disabled={disabled || !item.descriptionEditable || item.isPendingDelete}
            fullWidth
            label={descriptionLabel}
            maxRows={3}
            minRows={1}
            multiline
            size="small"
            value={item.description || ''}
            onChange={(event) => onDescriptionChange(item.id, event.target.value)}
          />
        ) : null}

        {extraFields.map((field) => {
          const fieldValue = item.extraFieldValues?.[field.key];

          if (field.type === 'checkbox') {
            return (
              <FormControlLabel
                key={field.key}
                control={
                  <Checkbox
                    checked={Boolean(fieldValue)}
                    disabled={disabled || item.isPendingDelete}
                    onChange={(event) => onExtraFieldChange(item.id, field.key, event.target.checked)}
                  />
                }
                label={field.label}
              />
            );
          }

          if (field.type === 'markdown') {
            return (
              <Box key={field.key}>
                <Typography sx={{ display: 'block', mb: 0.75 }} variant="body2">
                  {field.label}
                </Typography>
                <MarkdownEditor
                  disabled={disabled || item.isPendingDelete}
                  readonly={disabled || item.isPendingDelete}
                  value={typeof fieldValue === 'string' ? fieldValue : ''}
                  wrapperId={`flat-image-${item.id}-${field.key}`}
                  onBlur={(nextValue: string) => onExtraFieldChange(item.id, field.key, nextValue)}
                />
                <Typography color="warning.main" sx={{ display: 'block', mt: 0.75 }} variant="caption">
                  Changes in the markdown are not applied as you type. Make sure you click/tap outside the field before
                  submission.
                </Typography>
              </Box>
            );
          }

          return (
            <TextField
              key={field.key}
              disabled={disabled || item.isPendingDelete}
              fullWidth
              label={field.label}
              size="small"
              type={field.type === 'number' ? 'number' : 'text'}
              value={fieldValue ?? ''}
              onChange={(event) =>
                onExtraFieldChange(
                  item.id,
                  field.key,
                  field.type === 'number' && event.target.value !== ''
                    ? Number(event.target.value)
                    : event.target.value,
                )
              }
            />
          );
        })}
      </CardContent>
    </Card>
  );
}
